// ─── Public Status Page — a subscriber's own subscription ─────────────
//
// Two routes, one component, because they are two states of one thing:
//
//   /manage/<code>            what am I signed up for, and stop if I want
//   /manage?key=<manageKey>   the same page, reached by the confirm redirect
//   /unsubscribe/<code>       stop first, then offer to undo
//
// ── Why these pages had to exist ──
// The API for all of this was already complete, the URL slugs were already
// reserved, and the confirm route was ALREADY redirecting to `/manage?key=…`.
// What was missing was any route to receive it: Caddy rewrote the path to
// status.html and the router fell through to the overview, so a customer who
// clicked "confirm my subscription" landed on the plain status page with no
// acknowledgement that anything had happened, and every "Afmeld" link in every
// email did the same (b-01553).
//
// ── The credential ──
// The short code in the link IS the credential. It is unguessable, it does not
// expire (an unsubscribe link in a two-year-old email has to keep working), and
// resolving it MINTS a manage key server-side — see the header of
// services/status/subscribers.ts. That means anyone holding the link can change
// these settings, so the page says so rather than pretending otherwise.
//
// ── What it never shows ──
// The email address or phone number. The server deliberately omits it (a manage
// link can be forwarded), and the holder does not need it to tick boxes.

import { useCallback, useEffect, useState } from 'react';

import { da } from '../i18n/da';
import {
  fetchSubscription,
  postSubscriptionPreferences,
  postUnsubscribe,
  type StatusSubscriptionView,
} from '../lib/statusApi';
import { isPlainLeftClick } from '../router';
import { SIcon } from '../ui/SIcon';
import { SystemPicker } from './SystemPicker';
import type { SubscriptionPageProps } from '../types';

type Phase = 'loading' | 'ready' | 'notfound';

export function SubscriptionPage({
  mode,
  code,
  manageKey,
  linkError,
  onBack,
  backHref,
}: SubscriptionPageProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [view, setView] = useState<StatusSubscriptionView | null>(null);
  const [audienceIds, setAudienceIds] = useState<string[]>([]);
  const [componentIds, setComponentIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Set once the unsubscribe has actually been performed, so the page stops
   *  claiming to be mid-flight and can offer the undo. */
  const [stopped, setStopped] = useState(false);

  const credential = { code, key: manageKey };
  const hasCredential = Boolean(code || manageKey);

  // ── Load ────────────────────────────────────────────────────────────
  // On /unsubscribe the STOP happens first and the load second: someone who
  // clicked "unsubscribe" has already told us what they want, and making them
  // confirm on a page that then has to load is a worse answer than doing it and
  // offering the undo. The API is idempotent, so a double-loaded page is a no-op.
  useEffect(() => {
    if (linkError || !hasCredential) {
      setPhase('notfound');
      return;
    }
    let cancelled = false;
    setPhase('loading');
    void (async () => {
      if (mode === 'unsubscribe') {
        await postUnsubscribe(credential);
        if (cancelled) return;
        setStopped(true);
      }
      const loaded = await fetchSubscription(credential);
      if (cancelled) return;
      setView(loaded);
      setAudienceIds(
        loaded
          ? (loaded.audiences ?? [])
              .filter((a) => (loaded.subscriber.audiences ?? []).includes(a.slug))
              .map((a) => a.id)
          : [],
      );
      // Ids straight through — unlike audiences, which the DTO carries as slugs
      // and the page has to map back through the options list.
      setComponentIds(loaded?.subscriber.componentIds ?? []);
      // A resolved credential still renders the page even when the row is now
      // unsubscribed — that is the state the undo button acts on.
      setPhase(loaded ? 'ready' : 'notfound');
    })();
    return () => {
      cancelled = true;
    };
    // credential is derived from the two props; depending on the props directly
    // keeps this from re-running on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, code, manageKey, linkError, hasCredential]);

  const toggleAudience = useCallback((id: string) => {
    setAudienceIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
    setNotice(null);
  }, []);

  /** The picker hands back the WHOLE selection — ticking a parent takes its
   *  subtree with it, so this cannot be an id-at-a-time toggle. */
  const changeComponents = useCallback((next: string[]) => {
    setComponentIds(next);
    setNotice(null);
  }, []);

  const save = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const ok = await postSubscriptionPreferences({ ...credential, audienceIds, componentIds });
    setBusy(false);
    if (ok) setNotice(da.manageSaved);
    else setError(da.manageSaveFailed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceIds, componentIds, busy, code, manageKey]);

  const stop = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    await postUnsubscribe(credential);
    setBusy(false);
    setStopped(true);
    setView((prev) => (prev ? { ...prev, subscriber: { ...prev.subscriber, status: 'unsubscribed' } } : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, code, manageKey]);

  /** Re-subscribing is the same manage write with a status, so the row keeps its
   *  history and its audience selection rather than starting over. */
  const resubscribe = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    // status: 'confirmed' is what actually re-subscribes. Without it this was
    // just an audience save on an unsubscribed row — the button would report
    // success and change nothing.
    const ok = await postSubscriptionPreferences({ ...credential, audienceIds, componentIds, status: 'confirmed' });
    setBusy(false);
    if (!ok) {
      setError(da.manageSaveFailed);
      return;
    }
    const reloaded = await fetchSubscription(credential);
    setStopped(false);
    if (reloaded) setView(reloaded);
    setNotice(da.manageSaved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceIds, componentIds, busy, code, manageKey]);

  const backLink = (
    <a
      className="sp-back"
      href={backHref}
      onClick={(event) => {
        if (!isPlainLeftClick(event)) return;
        event.preventDefault();
        onBack();
      }}
    >
      <SIcon name="chevronLeft" size={16} />
      {da.back}
    </a>
  );

  if (phase === 'loading') {
    return (
      <div className="sp-detail">
        {backLink}
        <div className="sp-card sp-card-pad sp-loading" role="status" aria-live="polite">
          <SIcon name="spinner" size={20} className="sp-spin" />
          <span>{mode === 'unsubscribe' ? da.unsubscribeWorking : da.loading}</span>
        </div>
      </div>
    );
  }

  // A link that did not resolve. Deliberately the SAME message for expired,
  // already-removed and never-existed — the page must not be usable to probe
  // whether a code is real.
  if (phase === 'notfound' || !view) {
    return (
      <div className="sp-detail">
        {backLink}
        <div className="sp-card sp-card-pad sp-empty" role="alert">
          <SIcon name="unknown" size={24} />
          <p>{da.manageNotFound}</p>
        </div>
      </div>
    );
  }

  const isUnsubscribed = stopped || view.subscriber.status === 'unsubscribed';
  const isPending = view.subscriber.status === 'pending';
  // TERMINAL. A bounced address is on the suppression list, which always wins —
  // nothing will reach it again regardless of what this page offers, so it gets
  // neither the "confirmed" reassurance nor a re-subscribe button that could not
  // work. Without this branch it fell through to the happy path and told someone
  // their subscription was active while they received nothing.
  const isBounced = view.subscriber.status === 'bounced';

  // ── The unsubscribe landing ─────────────────────────────────────────
  // Already done by the time this renders. It is a receipt, not a form.
  // `!isBounced` so a bounced address does NOT get the receipt's undo button:
  // the suppression list always wins, so re-subscribing would report success and
  // change nothing. It falls through to the manage view, which says plainly why.
  if (mode === 'unsubscribe' && isUnsubscribed && !isBounced) {
    return (
      <article className="sp-detail">
        {backLink}
        <header className="sp-detail-head">
          <div className="sp-detail-title-row">
            <SIcon name="operational" size={26} style={{ color: 'var(--accent-success)' }} />
            <h1 className="sp-detail-title">{da.unsubscribeTitle}</h1>
          </div>
        </header>
        <div className="sp-card sp-card-pad">
          <p className="sp-sub-lead">{da.unsubscribeDone}</p>
          <p className="sp-help">{da.unsubscribeRegret}</p>
          <div className="sp-actions sp-actions--start">
            <button type="button" className="sp-btn sp-btn--primary" onClick={() => void resubscribe()} disabled={busy}>
              {busy ? da.subscribeSending : da.unsubscribeUndo}
            </button>
            <a className="sp-btn sp-btn--ghost" href={backHref}>
              {da.unsubscribeToOverview}
            </a>
          </div>
          {error && (
            <p className="sp-error" role="alert">
              <SIcon name="degraded" size={16} />
              {error}
            </p>
          )}
        </div>
      </article>
    );
  }

  // ── The manage page ─────────────────────────────────────────────────
  return (
    <article className="sp-detail">
      {backLink}

      <header className="sp-detail-head">
        <div className="sp-detail-title-row">
          <SIcon
            name={isUnsubscribed ? 'unknown' : 'operational'}
            size={26}
            style={{ color: isUnsubscribed ? 'var(--text-tertiary)' : 'var(--accent-success)' }}
          />
          <h1 className="sp-detail-title">{da.manageTitle}</h1>
        </div>
        <div className="sp-detail-meta">
          <span className="sp-detail-meta-item">
            <SIcon name={view.subscriber.channel === 'sms' ? 'phone' : 'mail'} size={14} />
            {view.subscriber.channel === 'sms' ? da.manageChannelSms : da.manageChannelEmail}
          </span>
        </div>
      </header>

      {/* The confirm redirect lands here, so this page is also where a
          just-confirmed subscriber is told it worked. */}
      {isBounced && (
        <div className="sp-card sp-card-pad sp-empty" role="alert">
          <SIcon name="degraded" size={24} />
          <p>{da.manageBounced}</p>
        </div>
      )}
      {!isUnsubscribed && !isPending && !isBounced && (
        <div className="sp-success" role="status">
          <SIcon name="operational" size={18} />
          <span>{da.manageConfirmed}</span>
        </div>
      )}
      {isPending && (
        <div className="sp-card sp-card-pad sp-empty" role="status">
          <p>{da.managePending}</p>
        </div>
      )}
      {isUnsubscribed && !isBounced && (
        <div className="sp-card sp-card-pad sp-empty" role="status">
          <p>{da.manageAlreadyUnsubscribed}</p>
          <button type="button" className="sp-btn sp-btn--primary" onClick={() => void resubscribe()} disabled={busy}>
            {busy ? da.subscribeSending : da.manageResubscribe}
          </button>
        </div>
      )}

      {!isUnsubscribed && !isBounced && (
        <div className="sp-card sp-card-pad">
          <p className="sp-sub-lead">{da.manageIntro}</p>

          {view.audiences.length > 0 && (
            <div className="sp-field" role="group" aria-labelledby="sp-manage-audiences">
              <span className="sp-label-text" id="sp-manage-audiences">
                {da.subscribeAudiences}
              </span>
              <p className="sp-help">{da.subscribeAudiencesHint}</p>
              <div className="sp-check-list">
                {view.audiences.map((audience) => (
                  <label className="sp-check-row" key={audience.id}>
                    <input
                      type="checkbox"
                      checked={audienceIds.includes(audience.id)}
                      onChange={() => toggleAudience(audience.id)}
                    />
                    {audience.name}
                  </label>
                ))}
              </div>
              <p className="sp-help" aria-live="polite">
                {audienceIds.length === 0 ? da.subscribeAudiencesAll : da.subscribeSystemsCount(audienceIds.length)}
              </p>
            </div>
          )}

          {/* The SAME picker the signup form uses — a subscriber changing their
              mind must see the tree they ticked, not a second rendering of it. */}
          <SystemPicker
            components={view.components ?? []}
            selected={componentIds}
            onChange={changeComponents}
            labelId="sp-manage-systems"
          />

          {notice && (
            <p className="sp-success" role="status">
              <SIcon name="operational" size={16} />
              {notice}
            </p>
          )}
          {error && (
            <p className="sp-error" role="alert">
              <SIcon name="degraded" size={16} />
              {error}
            </p>
          )}

          <div className="sp-actions sp-actions--start">
            <button type="button" className="sp-btn sp-btn--primary" onClick={() => void save()} disabled={busy}>
              {busy ? da.subscribeSending : da.manageSave}
            </button>
            {/* Destructive, so it is the QUIET one — and it is reversible on this
                same page, which is what keeps it from needing a confirmation. */}
            <button type="button" className="sp-btn sp-btn--ghost" onClick={() => void stop()} disabled={busy}>
              {da.manageUnsubscribe}
            </button>
          </div>

          <p className="sp-help sp-sub-note">{da.manageLinkNote}</p>
        </div>
      )}
    </article>
  );
}
